import React, { createContext, useContext, useState, useEffect } from 'react';
import { coursesService, sessionsService, homeworksService } from '../services/firestoreService';
import { auth } from '../config/firebase';

const DataContext = createContext();

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};

export const DataProvider = ({ children }) => {
  const [courses, setCourses] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [homeworks, setHomeworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check authentication status
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setIsAuthenticated(!!user);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    // Subscribe to real-time updates
    let unsubscribeCourses;
    let unsubscribeSessions;
    let unsubscribeHomeworks;

    try {
      unsubscribeCourses = coursesService.subscribeToCourses((coursesData) => {
        setCourses(coursesData);
        setLoading(false);
      }, (error) => {
        // Only log actual errors, not index warnings
        if (error.code !== 'failed-precondition') {
          console.error('Error subscribing to courses:', error);
        }
        setLoading(false);
      });

      unsubscribeSessions = sessionsService.subscribeToSessions((sessionsData) => {
        setSessions(sessionsData);
      }, (error) => {
        // Only log actual errors, not index warnings
        if (error.code !== 'failed-precondition') {
          console.error('Error subscribing to sessions:', error);
        }
      });

      // Only subscribe to homeworks if user is authenticated
      // Homeworks require authentication, but public pages don't need them
      if (isAuthenticated) {
        unsubscribeHomeworks = homeworksService.subscribeToHomeworks((homeworksData) => {
          setHomeworks(homeworksData);
        }, (error) => {
          // Only log actual errors, not permission or index warnings
          if (error.code !== 'permission-denied' && error.code !== 'failed-precondition') {
            console.error('Error subscribing to homeworks:', error);
          }
        });
      }
    } catch (error) {
      console.error('Error setting up Firestore subscriptions:', error);
      setLoading(false);
    }

    return () => {
      if (unsubscribeCourses) unsubscribeCourses();
      if (unsubscribeSessions) unsubscribeSessions();
      if (unsubscribeHomeworks) unsubscribeHomeworks();
    };
  }, [isAuthenticated]);

  return (
    <DataContext.Provider value={{ courses, sessions, homeworks, loading }}>
      {children}
    </DataContext.Provider>
  );
};

